import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  AutoComplete,
  Modal,
  DatePicker,
} from "antd";
import { useSelector, useDispatch } from "react-redux";
import { debounce, isEqual, pick } from "lodash";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { getUserInfo } from "../../utils/utils";
import {
  updateLeadDetails,
  resetLeadCreatedInfo,
} from "../../actionCreator/leadDetailsActionCreator";
import {
  getExecutiveEmployeeList,
  getUnqualifiedReasons,
  searchIndustry,
} from "../../actionCreator/leadListingActionCreater";
import { isValidEmail } from "../../utils";
import { useAlert } from "../../context/AlertProvider";
import CustomFields from "../leads-details/CustomFields";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  normalizeMobileNumber,
  prefixSelector,
  validateMobileNumber,
} from "../../utils/mobileValidation";
import { countries } from "../../constant/Countries";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import dayjs from "dayjs";
import { useLeadCustomFieldDefinitions } from "../../common/hooks/useLeadCustomFieldDefinitions";
import { enumFieldType } from "../../constant/WebToLead";
import {
  getLeadCustomFieldsState,
  isFieldMandatory,
} from "../../utils/leadCustomFields";
const { TextArea } = Input;

const CustomFieldValueEditor = ({ value, onChange, field }) => {
  const pickValues = (field?.pickValues || []).map((option) => {
    const v =
      typeof option === "object" ? option?.value || option?.label : option;
    return {
      label: v,
      value: v,
    };
  });

  switch (field?.fieldType) {
    case enumFieldType.TEXT:
    case enumFieldType.CURRENCY:
    case enumFieldType.URL:
      return (
        <Input
          value={value}
          placeholder={field?.fieldName}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case enumFieldType.TEXT_AREA:
      return (
        <TextArea
          value={value}
          rows={2}
          maxLength={500}
          placeholder={field?.fieldName}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case enumFieldType.INTEGER:
    case enumFieldType.PHONE:
      return (
        <Input
          type="number"
          value={value}
          placeholder={field?.fieldName}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case enumFieldType.EMAIL:
      return (
        <Input
          value={value}
          placeholder={field?.fieldName}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case enumFieldType.PICK_LIST:
      return (
        <Select
          value={value}
          className="tw-w-full"
          options={pickValues}
          onChange={onChange}
        />
      );
    case enumFieldType.MULTI_PICK_LIST:
      return (
        <Select
          mode="multiple"
          className="tw-w-full"
          placeholder="Please select"
          value={
            Array.isArray(value)
              ? value
              : String(value || "")
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean)
          }
          onChange={(val) => onChange(val.join(", "))}
          options={pickValues}
        />
      );
    case enumFieldType.DATE:
      return (
        <DatePicker
          value={value ? dayjs(value) : null}
          className="tw-w-full"
          onChange={(date) => onChange(date ? dayjs(date).format() : null)}
        />
      );
    default:
      return <Input value={value} onChange={(e) => onChange(e.target.value)} />;
  }
};

const LeadForm = ({ setLoading, closeModal, initialValues, isNewLead }) => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [moreInfo, setMoreInfo] = useState(false);
  const [leadRatingList, setLeadRatingList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [wtlCustomFields, setWtlCustomFields] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [isDuplicate, setIsduplicate] = useState(false);
  const { fields: leadCustomFieldDefinitions } =
    useLeadCustomFieldDefinitions();

  const handleCancel = () => setOpen(false);

  const leadRatingListing = useSelector(
    (state) => state.leadListingReducer.leadRatingList,
  );
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const unqualifiedReasons = useSelector(
    (state) => state.leadListingReducer.unqualifiedReasons,
  );
  const phoneNo = initialValues?.phoneNo?.split("/") || [];

  const formattedInitialValues = {
    ...initialValues,
    unqualifiedReason: initialValues?.unqualifiedReason?.reason,
    status: leadStatusListing?.find((i) => i?.id === initialValues?.status)
      ?.text,
    phPrefix: formatAndExtractCountryCode(phoneNo[0])?.countryLabel,
    phoneNo: formatAndExtractCountryCode(phoneNo[0])?.numberWithoutCountryCode,
    prefix: formatAndExtractCountryCode(initialValues?.mobile)?.countryLabel,
    mobile: formatAndExtractCountryCode(initialValues?.mobile)
      ?.numberWithoutCountryCode,
    birthDate: initialValues?.birthDate
      ? dayjs(initialValues?.birthDate, "YYYY-MM-DD")
      : null,
    anniversaryDate: initialValues?.anniversaryDate
      ? dayjs(initialValues?.anniversaryDate, "YYYY-MM-DD")
      : null,
  };

  useEffect(() => {
    const ratingItem = leadRatingListing.map((item) => ({
      label: item.ratingText,
      value: item.ratingText,
    }));
    setLeadRatingList(ratingItem);
  }, [leadRatingListing]);

  useEffect(() => {
    if (initialValues?.id) {
      setMoreInfo(true);
      setStatus(
        leadStatusListing?.find((d) => d?.id === initialValues?.status)?.text,
      );
      setReason(initialValues?.unqualifiedReason?.reason);
      setTimeout(() => {
        const customFieldValues = {};
        (initialValues?.wtlCustomFields || []).forEach((f) => {
          customFieldValues[f.fieldName] = f.fieldValue;
        });

        form.setFieldsValue({
          ...formattedInitialValues,
          wtlCustomFieldValues: customFieldValues,
        });
      }, 0);
    } else {
      form.setFieldsValue({
        phPrefix: "India",
        prefix: formattedInitialValues.prefix || "India",
        mobile: formattedInitialValues.mobile,
        firstname: formattedInitialValues.firstname,
        lastname: formattedInitialValues.lastname,
      });
    }
  }, [initialValues, form, leadStatusListing]);

  useEffect(() => {
    if (initialValues?.id) {
      setWtlCustomFields(
        Array.isArray(initialValues?.wtlCustomFields)
          ? initialValues.wtlCustomFields
          : [],
      );
      return;
    }

    const { leadCustomFields } = getLeadCustomFieldsState(
      leadCustomFieldDefinitions,
      [],
    );
    setWtlCustomFields(leadCustomFields);
  }, [
    initialValues?.id,
    initialValues?.wtlCustomFields,
    leadCustomFieldDefinitions,
  ]);

  useEffect(() => {
    if (initialValues?.id) {
      dispatch(getUnqualifiedReasons());
    }
  }, [dispatch, initialValues]);

  const updateLead = async (values) => {
    const payload = {
      ...values,
      phoneNo: values?.phoneNo
        ? "+" +
          countries?.find((d) => d?.label === values?.phPrefix)?.code +
          " " +
          values?.phoneNo
        : undefined,
      mobile: values?.mobile
        ? "+" +
          countries?.find((d) => d?.label === values?.prefix)?.code +
          " " +
          values?.mobile
        : undefined,
      fullname: `${values?.firstname || ""} ${values?.lastname || ""}`.trim(),
      userId: getUserInfo().id,
      industryId: industries?.find((d) => d?.name === values?.industryText)?.id,
      status: 11,
      wtlCustomFields: wtlCustomFields.map((field) => {
        const payloadField = { ...field };

        delete payloadField.pickValues;
        delete payloadField.userAccount;

        if (!initialValues?.id) {
          delete payloadField.id;
        }

        return {
          ...payloadField,
          fieldValue:
            values.wtlCustomFieldValues?.[field.fieldName] ?? field.fieldValue,
        };
      }),
      birthDate: values?.birthDate
        ? dayjs(values?.birthDate).format("YYYY-MM-DD")
        : null,
      anniversaryDate: values?.anniversaryDate
        ? dayjs(values?.anniversaryDate).format("YYYY-MM-DD")
        : null,
    };

    setLoading && setLoading(true);

    const res = await dispatch(
      updateLeadDetails(
        initialValues?.id
          ? {
              ...initialValues,
              ...payload,
              unqualifiedReason: {
                id: unqualifiedReasons?.find(
                  (d) => d?.reason === payload?.unqualifiedReason,
                )?.id,
                reason: payload?.unqualifiedReason,
              },
              status: leadStatusListing?.find((i) => i?.text === values?.status)
                ?.id,
            }
          : {
              ...payload,
              allowDuplicate: !!isDuplicate,
            },
        !!initialValues?.id,
      ),
    );
    if (res?.response?.status) {
      if (res?.response?.status === 403) {
        showAlert(
          "warning",
          "Permission Denied",
          !initialValues?.id
            ? "You do not have permission to create lead."
            : "You do not have permission to update lead.",
        );
        if (!initialValues?.id) {
          closeModal();
          setIsduplicate(false);
          form.resetFields();
        }
      } else if (res?.response?.data?.errorKey === "duplicateLeadWarning") {
        setIsduplicate(res?.response?.data?.title);
      } else {
        if (
          res?.response?.data?.title?.includes(
            "Subscriber ussage limit expired",
          )
        ) {
          showAlert(
            "error",
            "You have exceeded your maximum lead creation limit.",
          );
        } else {
          showAlert("error", res.response?.data?.title);
        }
      }
    } else {
      if (!initialValues?.id) {
        showAlert("success", "Lead created successfully.");
        dispatch(resetLeadCreatedInfo());
        const allParams = {};
        for (const [key, value] of searchParams.entries()) {
          allParams[key] = value;
        }

        dispatch(
          getExecutiveEmployeeList({
            ...allParams,
            page: allParams?.page ? allParams?.page - 1 : 0,
          }),
        );
        form.resetFields();
        searchParams.set("page", 1);
        navigate(`${location.pathname}?${searchParams.toString()}`);
        closeModal();
        setIsduplicate(false);
      } else {
        showAlert("success", "Lead updated successfully.");
        setHasUnsavedChanges(false);
        closeModal();
      }
    }
    setLoading && setLoading(false);
  };

  const onSearch = debounce(async (value) => {
    const res = await dispatch(searchIndustry(value));
    setIndustries(res);
  }, 500);

  const normalizeValues = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value === "" ? null : value,
      ]),
    );
  };

  return (
    <>
      <Form
        form={form}
        onFinish={updateLead}
        className="lead-form"
        layout="vertical"
        onValuesChange={(changedValues, allValues) => {
          if (initialValues?.id) {
            const normalizedValues = normalizeValues(allValues);
            const relevantInitialValues = pick(
              normalizeValues(formattedInitialValues),
              Object.keys(normalizedValues),
            );
            const hasChanged = !isEqual(
              normalizedValues,
              relevantInitialValues,
            );
            setHasUnsavedChanges(hasChanged);
          }
          setIsduplicate(false);
        }}
      >
        <div
          className={`tw-pt-5 tw-px-6 tw-pb-0 tw-overflow-y-auto ${
            !initialValues?.id ? "tw-max-h-[430px]" : ""
          }`}
        >
          <Row className="justify-content-between tw-items-center">
            <Col xs={24} md={isNewLead ? 4 : 5}>
              <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                First Name<span className="text-error">*</span>
              </label>
            </Col>
            <Col xs={24} md={isNewLead ? 19 : 8}>
              <Form.Item
                name="firstname"
                className="tw-mb-[15px]"
                rules={[{ required: true, message: "Please enter first name" }]}
              >
                <Input className="common-input" />
              </Form.Item>
            </Col>
            <Col xs={24} md={isNewLead ? 4 : 3}>
              <label
                className={`tw-text-sm tw-font-medium ${isNewLead ? "" : "tw-ml-4"} tw-mb-[15px]`}
              >
                Last Name<span className="text-error">*</span>
              </label>
            </Col>
            <Col xs={24} md={isNewLead ? 19 : 8}>
              <Form.Item
                name="lastname"
                className="tw-mb-[15px]"
                rules={[{ required: true, message: "Please enter last name" }]}
              >
                <Input className="common-input" />
              </Form.Item>
            </Col>
          </Row>
          {/* <Row className="justify-content-between"></Row> */}
          <Row className="justify-content-between tw-items-center">
            <Col xs={24} md={4}>
              <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                Designation
              </label>
            </Col>
            <Col xs={24} md={19}>
              <Form.Item name="title" className="tw-mb-[15px]">
                <Input className="common-input" />
              </Form.Item>
            </Col>
          </Row>
          <Row className="justify-content-between tw-items-center ">
            <Col xs={24} md={4}>
              <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                Mobile
              </label>
            </Col>
            <Col xs={24} md={19}>
              <Form.Item
                name="phoneNo"
                className="tw-mb-[15px]"
                normalize={normalizeMobileNumber}
                rules={[
                  {
                    validator: validateMobileNumber,
                  },
                ]}
              >
                <Input
                  className="common-input"
                  addonBefore={prefixSelector("phPrefix")}
                  maxLength={20}
                  onChange={(e) => handleMobileChange(e.target.value, form)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="justify-content-between tw-items-center">
            <Col xs={24} md={4}>
              <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                Mobile<span className="text-error">**</span>
              </label>
            </Col>
            <Col xs={24} md={19}>
              <Form.Item
                name="mobile"
                className="tw-mb-[15px]"
                normalize={normalizeMobileNumber}
                dependencies={["emailId"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const email = getFieldValue("emailId");
                      if (value || email) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        "Please enter mobile number or email",
                      );
                    },
                  }),
                  {
                    validator: validateMobileNumber,
                  },
                ]}
              >
                <Input
                  className="common-input mobile-no"
                  addonBefore={prefixSelector()}
                  maxLength={20}
                  onChange={(e) => handleMobileChange(e.target.value, form)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="justify-content-between tw-items-center">
            <Col xs={24} md={4}>
              <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                Email<span className="text-error">**</span>
              </label>
            </Col>
            <Col xs={24} md={19}>
              <Form.Item
                name="emailId"
                className="tw-mb-[15px]"
                dependencies={["mobile"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const mobile = getFieldValue("mobile");
                      if (value || mobile) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        "Please enter email or mobile number",
                      );
                    },
                  }),
                  {
                    validator: (_, value) =>
                      !value || isValidEmail(value)
                        ? Promise.resolve()
                        : Promise.reject("Please enter a valid email"),
                  },
                ]}
              >
                <Input className="common-input" />
              </Form.Item>
            </Col>
          </Row>
          <Row className="justify-content-between tw-items-center">
            <Col xs={24} md={4}>
              <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                Company Name
              </label>
            </Col>
            <Col xs={24} md={19}>
              <Form.Item name="companyName" className="tw-mb-[15px]">
                <PlaceAutocomplete
                  onPlaceSelect={(v) => {
                    form.setFieldsValue({
                      companyName: v?.name,
                      website: v?.website,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="justify-content-between">
            <Col xs={24} md={4}>
              <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                Website
              </label>
            </Col>
            <Col xs={24} md={19} className="d-flex align-items-center gap-10 ">
              <Form.Item name="website" className="tw-w-full tw-mb-[15px]">
                <Input className="common-input" />
              </Form.Item>
              {initialValues?.website && (
                <Link to={`http://${initialValues?.website}`} target="_blank">
                  <span className="text-black la  la-globe mr-2"></span>
                </Link>
              )}
            </Col>
          </Row>

          {wtlCustomFields
            .filter((field) => field.isMandatory || field.isOptional === false)
            .map((field, index) => {
              return (
                <Row
                  key={index}
                  className="justify-content-between tw-items-center mb15"
                >
                  <Col xs={24} md={4}>
                    <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                      {field.fieldName}
                      <span className="text-error">*</span>
                    </label>
                  </Col>
                  <Col xs={24} md={19}>
                    <Form.Item
                      name={["wtlCustomFieldValues", field.fieldName]}
                      className="tw-mb-[15px]"
                      rules={[
                        {
                          required: true,
                          message: `${field.fieldName} is required`,
                        },
                      ]}
                      initialValue={field.fieldValue}
                    >
                      <CustomFieldValueEditor
                        field={field}
                        onChange={(value) => {
                          const updatedValues = wtlCustomFields.map((d) =>
                            d?.fieldName === field.fieldName
                              ? { ...d, fieldValue: value }
                              : d,
                          );
                          setWtlCustomFields(updatedValues);
                          if (initialValues?.id) {
                            setHasUnsavedChanges(true);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              );
            })}

          {moreInfo && (
            <>
              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Birth Date
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item name="birthDate" className="tw-mb-[15px]">
                    <DatePicker
                      disabledDate={(current) => current > dayjs().endOf("day")}
                      className="tw-w-full"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Anniversary Date
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item name="anniversaryDate" className="tw-mb-[15px]">
                    <DatePicker className="tw-w-full" />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Address
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item name="address" className="tw-mb-[15px]">
                    <Input.TextArea
                      rows={2}
                      maxLength={100}
                      className="common-input"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Industry
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item name="industryText" className="tw-mb-[15px]">
                    <AutoComplete
                      options={industries?.map((d) => ({
                        label: d.name,
                        value: d.name,
                      }))}
                      onSearch={onSearch}
                      className="tw-border-none"
                    >
                      <Input
                        className="common-input !tw-border-none tw-py-[4.2px] tw-px-[11px] tw-text-[12px] tw-h-full"
                        placeholder="Start typing a industry name"
                        allowClear
                      />
                    </AutoComplete>
                  </Form.Item>
                </Col>
              </Row>
              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Potential Revenue (₹)
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item
                    name="rate"
                    className="tw-mb-[15px]"
                    rules={[
                      {
                        pattern: /^\d+(\.\d{1,2})?$/,
                        message: "Please enter valid revenue",
                      },
                    ]}
                  >
                    <Input className="common-input" />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="justify-content-between">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Opportunity Amount (₹)
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item
                    name="oppurtunityAmount"
                    className="tw-mb-[15px]"
                    rules={[
                      {
                        pattern: /^\d+(\.\d{1,2})?$/,
                        message: "Please enter valid opportunity amount",
                      },
                    ]}
                  >
                    <Input className="common-input" />
                  </Form.Item>
                </Col>
              </Row>
              {!!initialValues?.id && (
                <>
                  <Row className="justify-content-between tw-items-center">
                    <Col xs={24} md={4}>
                      <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                        Lead Status
                      </label>
                    </Col>
                    <Col xs={24} md={19}>
                      <Form.Item
                        name="status"
                        className="tw-mb-[15px]"
                        normalize={(value) => value?.replace(/^\s+/, "")}
                        rules={[
                          { required: true, message: "This field is required" },
                        ]}
                      >
                        <Select
                          placeholder="Select Status"
                          options={leadStatusListing?.map((d) => ({
                            label: d.text,
                            value: d.text,
                          }))}
                          onChange={(value) => {
                            form.setFieldsValue({
                              lostReason: null,
                              comments: null,
                              unqualifiedReason: null,
                              unqualifiedReasonsOthers: null,
                              status: value,
                            });
                            setStatus(value);
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {status === "Closed Lost" && (
                    <>
                      <Row className="justify-content-between tw-items-center">
                        <Col xs={24} md={4}>
                          <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                            Lost Reason
                          </label>
                        </Col>
                        <Col xs={24} md={19}>
                          <Form.Item
                            name="lostReason"
                            className="tw-mb-[15px]"
                            rules={[
                              {
                                required: true,
                                message: "Please add a reason",
                              },
                            ]}
                          >
                            <Input
                              className="common-input"
                              placeholder="Enter reason"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row className="justify-content-between tw-items-center">
                        <Col xs={24} md={4}>
                          <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                            Lost Reason Comments
                          </label>
                        </Col>
                        <Col xs={24} md={19}>
                          <Form.Item name="comments" className="tw-mb-[15px]">
                            <TextArea
                              className="common-input"
                              rows={3}
                              placeholder="Enter comments"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}

                  {status === "Un-Qualified" && (
                    <>
                      <Row className="justify-content-between tw-items-center">
                        <Col xs={24} md={4}>
                          <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                            Reason for Unqualification
                          </label>
                        </Col>
                        <Col xs={24} md={19}>
                          <Form.Item
                            name="unqualifiedReason"
                            className="tw-mb-[15px]"
                            rules={[
                              {
                                required: true,
                                message: "Please select a reason",
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select reason"
                              options={unqualifiedReasons?.map((d) => ({
                                label: d.reason,
                                value: d.reason,
                              }))}
                              onChange={(value) => {
                                form.setFieldsValue({
                                  unqualifiedReason: value,
                                  unqualifiedReasonsOthers: undefined,
                                });
                                setReason(value);
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {reason === "Others" && (
                        <Row className="justify-content-between tw-items-center]">
                          <Col xs={24} md={4}>
                            <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                              Add Reason for Unqualification
                            </label>
                          </Col>
                          <Col xs={24} md={19}>
                            <Form.Item
                              className="tw-mb-[15px]"
                              name="unqualifiedReasonsOthers"
                              rules={[
                                {
                                  required: true,
                                  message: "Please add a reason",
                                },
                              ]}
                            >
                              <TextArea
                                className="common-input"
                                rows={3}
                                placeholder="Enter unqualification reason"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </>
              )}
              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Lead Source
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item name="leadSource" className="tw-mb-[15px]">
                    <Input className="common-input" />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Lead Description
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item name="description" className="tw-mb-[15px]">
                    <Input.TextArea rows={2} className="common-input" />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="justify-content-between tw-items-center">
                <Col xs={24} md={4}>
                  <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                    Lead Rating
                  </label>
                </Col>
                <Col xs={24} md={19}>
                  <Form.Item name="leadRating" className="tw-mb-[15px]">
                    <Select
                      showSearch
                      options={leadRatingList}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              {wtlCustomFields
                .filter(
                  (field) => !(field.isMandatory || field.isOptional === false),
                )
                .map((field, index) => {
                  return (
                    <Row
                      key={index}
                      className="justify-content-between tw-items-center mb15"
                    >
                      <Col xs={24} md={4}>
                        <label className="tw-text-sm tw-font-medium tw-mb-[15px]">
                          {field.fieldName}
                        </label>
                      </Col>
                      <Col xs={24} md={19}>
                        <Form.Item
                          name={["wtlCustomFieldValues", field.fieldName]}
                          className="tw-mb-[15px]"
                          rules={[
                            {
                              required: false,
                            },
                          ]}
                          initialValue={field.fieldValue}
                        >
                          <CustomFieldValueEditor
                            field={field}
                            onChange={(value) => {
                              const updatedValues = wtlCustomFields.map((d) =>
                                d?.fieldName === field.fieldName
                                  ? { ...d, fieldValue: value }
                                  : d,
                              );
                              setWtlCustomFields(updatedValues);
                              if (initialValues?.id) {
                                setHasUnsavedChanges(true);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                })}
            </>
          )}
        </div>
        {!initialValues?.id && (
          <div
            className="d-flex heading13px cursor-pointer pr20 justify-content-end text-theme mb20 mt10"
            onClick={() => setMoreInfo(!moreInfo)}
          >
            {moreInfo ? "View Less" : "View More"}
          </div>
        )}
        {isDuplicate ? (
          <>
            <div className="tw-px-5 tw-font-semibold">{isDuplicate}</div>
            <div className="d-flex gap-20 pr20 mt20 mb30 tw-pb-4 justify-content-end align-items-center">
              <Button
                className="secondary-btn"
                onClick={() => {
                  closeModal();
                  setIsduplicate(false);
                }}
              >
                No
              </Button>
              <Button htmlType="submit" type="primary" className="theme-btn">
                Yes
              </Button>
            </div>
          </>
        ) : (
          <div
            className={
              isNewLead
                ? "tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50"
                : "d-flex gap-20 pr20 mt20 mb30 tw-pb-4 justify-content-end align-items-center"
            }
          >
            {initialValues?.id &&
              !initialValues?.allowedActions?.includes("EDIT") && (
                <div className="text-color-717171">
                  You don’t have permission to update this lead.
                </div>
              )}
            {(hasUnsavedChanges || !initialValues?.id) && (
              <Button
                disabled={
                  initialValues?.id &&
                  !initialValues?.allowedActions?.includes("EDIT")
                }
                className={
                  isNewLead
                    ? "!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
                    : "tw-border-sub-text hover:!tw-border-sub-text tw-text-sm tw-text-sub-text hover:!tw-text-sub-text"
                }
                onClick={() => {
                  if (!initialValues?.id) {
                    closeModal();
                  } else {
                    if (hasUnsavedChanges) {
                      setOpen(true);
                    }
                  }
                }}
              >
                {!initialValues?.id ? "Cancel" : "Discard"}
              </Button>
            )}

            <Button
              htmlType="submit"
              type="primary"
              className={
                isNewLead
                  ? "!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
                  : "!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm tw-px-10"
              }
            >
              Save
            </Button>
          </div>
        )}
      </Form>
      <Modal
        open={isOpen}
        centered
        footer={null}
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        <div>Are you sure you want to discard your unsaved changes?</div>
        <div className="mt20 d-flex justify-content-end align-items-center mb10 tw-gap-5">
          <Button className="light-btn" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            className="stop-sync"
            onClick={() => {
              form.setFieldsValue({
                ...formattedInitialValues,
              });
              setWtlCustomFields(
                initialValues?.id
                  ? Array.isArray(initialValues?.wtlCustomFields)
                    ? initialValues.wtlCustomFields
                    : []
                  : getLeadCustomFieldsState(leadCustomFieldDefinitions, [])
                      .leadCustomFields,
              );
              handleCancel();
              setHasUnsavedChanges(false);
            }}
          >
            Discard
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default LeadForm;
